import React, { useState } from "react";
import DataFetcherTemplateHome from "../../hooks/DataFetcherTemplateHome";
import {
  BookOpen,
  Globe,
  FileCheck,
  Briefcase,
  Award,
  Users,
  Handshake,
  Building2,
  RotateCcw,
} from "lucide-react";
import BlockAcademicOffer from "../../components/BlockAcademicOffer";

const BASE_URL = "https://be-ofertas.utpl.edu.ec/";

// 1. Construcción limpia de la URL de Drupal JSON:API
const endpoint = new URL(
  "jsonapi/node/template_home/8d79aff7-681f-4fa5-abaa-bd7557bcbe50",
  BASE_URL,
);
endpoint.searchParams.set(
  "include",
  "field_imagen_portada.field_media_image,field_gallery.field_media_image,field_imagen1.field_media_image,field_imagen2.field_media_image,field_imagen3.field_media_image,field_imagen4.field_media_image,field_imagen5.field_media_image,field_imagen6.field_media_image,field_imagen7.field_media_image," +
    "field_imagen8.field_media_image",
);
endpoint.searchParams.set(
  "fields[node--template_home]",
  "title,field_gallery,field_imagen_portada,field_imagen1,field_imagen2,field_imagen3,field_imagen4,field_imagen5,field_imagen6,field_imagen7,field_imagen8,field_texto_portada,field_bloque1,field_bloque2,field_bloque3,field_bloque4,field_bloque5,field_bloque6,field_bloque7,field_bloque8,field_bloque9," +
    "field_bloque10,field_bloque11,field_bloque12,field_bloque13,field_bloque14,field_bloque15,field_bloque16,field_bloque17,field_bloque18,field_bloque19",
);
endpoint.searchParams.set("fields[media--image]", "field_media_image");
endpoint.searchParams.set("fields[file--file]", "uri");

const url = endpoint.toString();

// 2. Sanitizador / transformador de rutas de imágenes y enlaces compatible con SSR
const processContent = (html = "") => {
  if (typeof window === "undefined" || !html) return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Procesar imágenes con rutas relativas
    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src");
      if (src && !src.startsWith("http")) {
        try {
          img.setAttribute("src", new URL(src, BASE_URL).href);
        } catch {
          // Conserva el src original si la URL es inválida
        }
      }
    });

    // Procesar enlaces <a> para rutas relativas
    doc.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href");
      if (
        href &&
        !href.startsWith("http") &&
        !href.startsWith("#") &&
        !href.startsWith("mailto:")
      ) {
        try {
          a.setAttribute("href", new URL(href, BASE_URL).href);
        } catch {
          // Conserva el href original si es inválido
        }
      }
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

const CARD_STYLES = {
  container:
    "group relative min-h-[200px] sm:min-h-[400px] rounded-lg overflow-hidden flex flex-col justify-end p-6",
  image:
    "absolute inset-0 w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105",
  overlay:
    "absolute inset-0 bg-[#004270] sm:bg-gradient-to-b sm:from-transparent sm:via-[#004270]/50 sm:to-[#004270] opacity-90 sm:opacity-10 group-hover:sm:opacity-40 transition-opacity duration-300 ",
  content:
    "relative z-1 text-center text-white w-full flex flex-col items-center justify-end h-full " +
    "[&_p]:text-white [&_p]:p-0 [&_p]:text-xl [&_p]:font-bold " +
    "[&_a]:absolute [&_a]:inset-0 [&_a]:z-30 [&_a]:text-transparent [&_a]:overflow-hidden",
};

// Definición de estilos de tarjeta reutilizables en una variable
const cardStyles =
  "bg-white rounded-2xl p-4 shadow-sm border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all duration-200 group";

const iconContainerStyles =
  "w-12 h-12 rounded-xl bg-slate-100 text-[#004270] flex items-center justify-center mb-4 group-hover:bg-[#004270] group-hover:text-white transition-colors duration-200";

export function SeccionBloques({ data }) {
  if (!data) return null;

  const indices = [3, 4, 5, 6, 7, 8];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {indices.map((num) => {
        const contenido = data[`bloque${num}`];
        const imagen = data[`imagen${num}`];

        if (!contenido) return null;

        return (
          <div key={num} className={CARD_STYLES.container}>
            {imagen && (
              <img
                src={imagen}
                alt={`Contenido institucional ${num} UTPL`}
                className={CARD_STYLES.image}
                loading="lazy"
              />
            )}

            <div className={CARD_STYLES.overlay} />

            <div
              className={CARD_STYLES.content}
              dangerouslySetInnerHTML={{
                __html: processContent(contenido),
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

function DataDisplay({ data, selectedNivel }) {
  if (!data) return null;

  return (
    <div>
      {data.bloque1 && (
        <div
          className="max-w-3xl mx-auto"
          dangerouslySetInnerHTML={{
            __html: processContent(data.bloque1),
          }}
        />
      )}

      {/*OFERTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl m-auto px-6 sm:px-0">
          {data.bloque2 && (
            <div
              className="text-center max-w-3xl m-auto "
              dangerouslySetInnerHTML={{
                __html: processContent(data.bloque2),
              }}
            />
          )}  </div>
          <BlockAcademicOffer></BlockAcademicOffer>
            <div className="max-w-6xl m-auto px-6 sm:px-0">
          <SeccionBloques data={data} /></div>
      
      </section>

      {/*FORMAS DE INGRESAR */}
      <section className="py-16 link-1">
        <div className="max-w-6xl m-auto px-6 sm:px-0">
          {data.bloque9 && (
            <div
              className="text-center max-w-3xl m-auto mb-4 [&_h3_strong]:font-extrabold [&_h3_strong]:text-4xl [&_h3_strong]:text-[#febe10]"
              dangerouslySetInnerHTML={{
                __html: processContent(data.bloque9),
              }}
            />
          )}

          <div
            className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2
          [&_h3]:text-lg
          [&_h3_strong]:text-lg
          [&_h3]:m-0
          [&_h3]:p-0
          [&_p]:text-sm"
          >
            {data.bloque10 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <BookOpen className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque10),
                  }}
                />
              </div>
            )}
            {data.bloque11 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <Globe className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque11),
                  }}
                />
              </div>
            )}
            {data.bloque12 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <FileCheck className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque12),
                  }}
                />
              </div>
            )}
            {data.bloque13 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <Briefcase className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque13),
                  }}
                />
              </div>
            )}
            {data.bloque14 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <Award className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque14),
                  }}
                />
              </div>
            )}
          </div>

          {data.bloque15 && (
            <div
              className="text-center max-w-3xl m-auto mt-16 mb-4 [&_h3_strong]:font-extrabold [&_h3_strong]:text-4xl [&_h3_strong]:text-[#febe10]"
              dangerouslySetInnerHTML={{
                __html: processContent(data.bloque15),
              }}
            />
          )}

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6
          [&_h3]:text-lg
          [&_h3_strong]:text-lg
          [&_h3]:m-0
          [&_h3]:p-0
          [&_p]:text-sm"
          >
            {data.bloque16 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <Users className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque16),
                  }}
                />
              </div>
            )}
            {data.bloque17 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <Handshake className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque17),
                  }}
                />
              </div>
            )}
            {data.bloque18 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <Building2 className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque18),
                  }}
                />
              </div>
            )}
            {data.bloque19 && (
              <div className={cardStyles}>
                <div className={iconContainerStyles}>
                  <RotateCcw className="w-6 h-6" />
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: processContent(data.bloque19),
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// 6. Home principal
function Home() {
  const [selectedNivel, setNivel] = useState(undefined);

  return (
    <DataFetcherTemplateHome url={url}>
      {(data) => <DataDisplay data={data} selectedNivel={selectedNivel} />}
    </DataFetcherTemplateHome>
  );
}

export default Home;
